import PropTypes from 'prop-types';
import React, { forwardRef, useMemo, useState, useEffect, useRef } from 'react';
import { Box, Stack, Dialog, Button, AppBar, Typography, Slide, List, ListItem, ListItemText, useTheme } from '@mui/material';
import { setCurrentTreatmentDataGlobal } from 'src/redux/slices/multiTreatment';
import { setOpenCallScriptFromLeadPage, setCallScriptSection } from 'src/redux/slices/callScript';
import { Iconify } from 'src/components';
import { CallScriptListing } from '../followup-callScript';
import { CallScriptV2 } from '../callScriptV2';
import { transformScriptData } from '../callScriptV2/utils';
import { clearLead, clearTaskLead } from '../../redux/slices/lead';
import { dispatch, useSelector } from '../../redux/store';
import { callApi } from '../../utils/apiCall';
import {
    commonDialogHeaderStyle,
    reminderTitleStyle,
    cancelBoxStyle,
    reminderDialogStyle,
} from '../../styles/ScheduleReminder';
import { scriptStackStyle, callScriptListItemStyle } from '../../styles/CallScripts';
import capitalizeFirstLetter from '../../utils/capitalizedFunc';

const Transition = forwardRef((props, ref) => <Slide direction='up' ref={ref} {...props} />);

/**
 * CallScriptContainer - A generic fullscreen container for Call Scripts
 *
 * This component wraps the CallScriptListing component in a fullscreen dialog.
 * It handles the display of call scripts (single or multiple) and provides
 * a consistent fullscreen experience with proper exit handling.
 *
 * @param {boolean} open - Controls the visibility of the dialog
 * @param {function} onClose - Callback when the dialog is closed (exits fullscreen)
 * @param {array} scriptList - Array of call script objects to display
 * @param {object} followLead - The lead object for the call script context
 * @param {function} onOpenAppointmentNotBooked - Callback when appointment is not booked
 * @param {function} onOpenAppointmentBooked - Callback when appointment is booked
 * @param {function} setLeadId - Callback to set the lead ID
 * @param {function} setTreatmentId - Callback to set the treatment ID
 * @param {string} type - Type of script (e.g., "TCO" for TCO scripts)
 * @param {string} title - Custom title for the dialog header
 * @param {boolean} headerButtons - Whether to show header action buttons
 * @param {boolean} useV2 - Whether to use the new V2 UI (default: false)
 * @param {string} callType - Type of call ('inbound' or 'outbound') for V2 UI
 * @param {string} scriptCategory - Script category ('INBOUND', 'OUTBOUND', 'TCO') for outcome button logic
 */
const CallScriptContainer = ({
    open,
    onClose,
    scriptList,
    followLead,
    onOpenAppointmentNotBooked,
    onOpenAppointmentBooked,
    onOpenUnableToContact,
    setLeadId,
    setTreatmentId,
    type,
    title,
    headerButtons = true,
    useV2 = false,
    callType = 'inbound',
    scriptCategory,
}) => {
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const isTCO = type === 'TCO' || scriptCategory === 'TCO';
    const [scrollableView, setScrollableView] = useState(isTCO);

    // V2 script selection state — null means no script selected yet (show picker)
    const [selectedV2Script, setSelectedV2Script] = useState(null);
    const hasMultipleScripts = useV2 && scriptList?.length > 1;

    // Auto-select when only one script exists
    useEffect(() => {
        if (useV2 && scriptList?.length === 1) {
            setSelectedV2Script(scriptList[0]);
        }
    }, [useV2, scriptList]);

    // Reset selection when dialog closes
    useEffect(() => {
        if (!open) {
            setSelectedV2Script(null);
        }
    }, [open]);

    // Fetch call script config from Practice Choices (skip for TCO — always scrollable)
    useEffect(() => {
        if (isTCO) {
            setScrollableView(true);
            return;
        }
        if (!practiceId || !open) return;
        callApi(`/api/v1/practices/${practiceId}/choices/?ordering=-created_at`)
            .then((res) => {
                if (res.ok) {
                    const choices = res.data?.results || res.data || [];
                    const callScriptChoice = choices.find((c) => {
                        const m = typeof c?.metadata === 'string'
                            ? (() => { try { return JSON.parse(c.metadata || '{}'); } catch { return {}; } })()
                            : (c?.metadata || {});
                        return m.type === 'call_script_config';
                    });
                    if (callScriptChoice) {
                        const meta = typeof callScriptChoice.metadata === 'string'
                            ? JSON.parse(callScriptChoice.metadata)
                            : callScriptChoice.metadata;
                        setScrollableView(meta?.default_view === 'scrollable');
                    }
                }
            })
            .catch((err) => { console.error('CallScriptContainer: choices fetch failed', err); });
    }, [practiceId, open, isTCO]);

    const handleClose = (reason, reloadPayload) => {
        // Only perform cleanup Redux dispatches when finishing (not when canceling)
        // This prevents parent dialogs from closing due to state changes
        if (reason === 'finish') {
            dispatch(clearLead());
            dispatch(clearTaskLead());
            dispatch(setCurrentTreatmentDataGlobal(null));
        }
        dispatch(setOpenCallScriptFromLeadPage(false));
        // Always clear the script payload from Redux on close — both cancel and finish.
        // CallScriptV2's internal effect that nulls callScriptData (CallScriptV2.jsx:893,969)
        // can early-return when no treatment is selected, leaving outbound's payload to leak
        // into the next inbound mount and durably break it until a hard refresh.
        dispatch(setCallScriptSection(null));
        // Always call onClose so parent can close the call script dialog
        // Parent should use reason to decide: 'cancel' = only close script, 'finish' = close all
        if (onClose) {
            onClose(reason, reloadPayload);
        }
    };

    const handleCallScriptClose = (event, reason) => {
        // Prevent closing on backdrop click or escape key to avoid accidental data loss
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        // Stop event propagation and prevent default to avoid triggering parent dialog close
        if (event?.stopPropagation) {
            event.stopPropagation();
        }
        if (event?.preventDefault) {
            event.preventDefault();
        }
        handleClose('cancel');
    };

    const getTitle = () => {
        if (title) return title;
        if (type === 'TCO') return 'TCO Scripts';
        return 'Call Scripts';
    };

    // Transform script data for V2 UI.
    // Use a ref to persist the transformed data across renders — CallScriptV2 internally
    // dispatches setCallScriptSection(null) which clears the Redux callScriptData that
    // TCOScript reads for the scriptList prop. By storing in a ref and computing synchronously
    // during render (not in an async useEffect), the data is available on the FIRST render
    // after scriptList arrives, before CallScriptV2's internal effects can clear it.
    const v2ScriptDataRef = useRef(null);
    const v2ScriptIdRef = useRef(null);

    if (useV2 && selectedV2Script?.id) {
        if (selectedV2Script.id !== v2ScriptIdRef.current) {
            v2ScriptIdRef.current = selectedV2Script.id;
            v2ScriptDataRef.current = transformScriptData(selectedV2Script, callType);
        }
    }
    const v2ScriptData = v2ScriptDataRef.current;

    // Derive script category from props or script data
    const effectiveScriptCategory = useMemo(() => {
        if (scriptCategory) return scriptCategory;
        // Try to get from script data
        const script = scriptList?.[0];
        if (script?.category) return script.category.toUpperCase();
        // Map from type prop (e.g., 'TCO' -> 'TCO')
        if (type === 'TCO') return 'TCO';
        // Map from callType
        return callType === 'outbound' ? 'OUTBOUND' : 'INBOUND';
    }, [scriptCategory, scriptList, type, callType]);

    // Lead data for V2 context panel
    const v2LeadData = useMemo(() => ({
        name: followLead?.first_name
            ? `${followLead.first_name} ${followLead.last_name || ''}`.trim()
            : 'Unknown Caller',
        initials: followLead?.first_name
            ? `${followLead.first_name[0]}${followLead.last_name?.[0] || ''}`.toUpperCase()
            : '?',
        tags: followLead?.treatment
            ? [{ label: followLead.treatment, variant: 'treatment' }]
            : [],
    }), [followLead]);

    // V2 handlers
    const handleV2Complete = (data) => {
        handleClose('finish', data);
    };

    const handleV2AppointmentBooked = () => {
        const leadId = followLead?.id || followLead?.lead_id;
        const treatmentId = followLead?.lead_treatments?.[0]?.id || followLead?.treatment_id;
        if (setLeadId && leadId) {
            setLeadId(leadId);
        }
        if (setTreatmentId && treatmentId) {
            setTreatmentId(treatmentId);
        }
        onOpenAppointmentBooked?.();
    };

    const handleV2AppointmentNotBooked = () => {
        const leadId = followLead?.id || followLead?.lead_id;
        const treatmentId = followLead?.lead_treatments?.[0]?.id || followLead?.treatment_id;
        if (setLeadId && leadId) {
            setLeadId(leadId);
        }
        if (setTreatmentId && treatmentId) {
            setTreatmentId(treatmentId);
        }
        onOpenAppointmentNotBooked?.();
    };

    const handleV2UnableToContact = () => {
        const leadId = followLead?.id || followLead?.lead_id;
        const treatmentId = followLead?.lead_treatments?.[0]?.id || followLead?.treatment_id;
        if (setLeadId && leadId) {
            setLeadId(leadId);
        }
        if (setTreatmentId && treatmentId) {
            setTreatmentId(treatmentId);
        }
        onOpenUnableToContact?.();
    };

    // Handle V2 script selection from picker
    const handleV2ScriptSelect = (script) => {
        const sorted = { ...script, sections: [...(script?.sections || [])].sort((a, b) => a.order - b.order) };
        setSelectedV2Script(sorted);
    };

    // Handle back from V2 to picker (only when multiple scripts)
    const handleV2BackToPicker = (e) => {
        if (e?.stopPropagation) e.stopPropagation();
        if (e?.preventDefault) e.preventDefault();
        // Clear the transform cache so the new script gets transformed
        v2ScriptIdRef.current = null;
        v2ScriptDataRef.current = null;
        setSelectedV2Script(null);
    };

    // Render V2 UI if enabled
    if (useV2) {
        // Show script picker when multiple scripts exist and none selected
        const showPicker = hasMultipleScripts && !selectedV2Script;

        return (
            <>
                {/* Small centered dialog for script selection (only when multiple scripts) */}
                <Dialog
                    open={open && showPicker}
                    onClose={handleCallScriptClose}
                    PaperProps={{
                        sx: {
                            width: '100%',
                            maxWidth: 560,
                            borderRadius: '16px',
                            m: 2,
                        }
                    }}
                >
                    <Box sx={{ p: '15px 16px', borderBottom: 'solid 1px rgba(145, 158, 171, 0.24)', position: 'relative' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: '700', textAlign: 'center' }}>
                            {isTCO ? 'TCO Scripts' : 'Call Scripts'}
                        </Typography>
                        <Box
                            sx={{
                                position: 'absolute',
                                right: 24,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                lineHeight: 0,
                                cursor: 'pointer',
                                color: 'text.secondary',
                            }}
                            onClick={handleCallScriptClose}
                        >
                            <Iconify icon='eva:close-fill' width='20px' height='20px' />
                        </Box>
                    </Box>
                    <Box sx={{ p: '24px' }}>
                        <List sx={{ m: 0, p: 0 }}>
                            {scriptList?.map((script, index) => (
                                <ListItem
                                    key={script.id || index}
                                    sx={callScriptListItemStyle(isLight)}
                                    onClick={() => handleV2ScriptSelect(script)}
                                >
                                    <ListItemText
                                        primary={capitalizeFirstLetter(script.name)}
                                        sx={{ '.MuiTypography-root': { fontSize: '13px', fontWeight: '700' } }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Iconify icon='akar-icons:chevron-right' width='20px' height='20px' />
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Dialog>

                {/* Fullscreen dialog for the actual V2 script UI */}
                <Dialog
                    fullScreen
                    TransitionComponent={Transition}
                    open={open && !showPicker}
                    onClose={handleCallScriptClose}
                    sx={{ '& .MuiDialog-paper': { bgcolor: '#F4F6F8' } }}
                >
                    <CallScriptV2
                        // Force a fresh CallScriptV2 instance whenever the script identity
                        // or call direction changes. Belt-and-braces against any future
                        // local-state leakage between sessions (see CallScriptContainer
                        // / OpenCallScriptPopup state-bleed bug).
                        key={`${callType}-${v2ScriptIdRef.current ?? 'none'}`}
                        scriptData={v2ScriptData}
                        leadData={v2LeadData}
                        followLead={followLead}
                        type={callType}
                        scriptCategory={effectiveScriptCategory}
                        onClose={hasMultipleScripts ? handleV2BackToPicker : handleCallScriptClose}
                        onSave={() => {}}
                        onComplete={handleV2Complete}
                        onAppointmentBooked={handleV2AppointmentBooked}
                        onAppointmentNotBooked={handleV2AppointmentNotBooked}
                        onUnableToContact={handleV2UnableToContact}
                        scrollableView={scrollableView}
                    />
                </Dialog>
            </>
        );
    }

    // Legacy UI
    return (
        <Dialog
            fullScreen
            TransitionComponent={Transition}
            open={open}
            onClose={handleCallScriptClose}
            sx={reminderDialogStyle(type)}
        >
            <AppBar sx={commonDialogHeaderStyle}>
                <Typography
                    sx={reminderTitleStyle}
                    variant='h6'
                    component='div'
                >
                    {getTitle()}
                </Typography>
                {headerButtons && (
                    <Box sx={cancelBoxStyle}>
                        <Button
                            color='inherit'
                            variant='outlined'
                            sx={{ borderColor: 'common.white', color: 'common.white' }}
                            onClick={handleCallScriptClose}
                        >
                            Cancel
                        </Button>
                    </Box>
                )}
            </AppBar>
            <Stack spacing={2} sx={scriptStackStyle}>
                <Box sx={{ position: 'relative', width: '100%' }}>
                    <CallScriptListing
                        type={type}
                        scriptList={scriptList}
                        setCallScriptActive={handleCallScriptClose}
                        onClose={handleClose}
                        followLead={followLead}
                        onOpenAppointmentNotBooked={onOpenAppointmentNotBooked}
                        onOpenAppointmentBooked={onOpenAppointmentBooked}
                        setLeadId={setLeadId}
                        setTreatmentId={setTreatmentId}
                    />
                </Box>
            </Stack>
        </Dialog>
    );
};

CallScriptContainer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    scriptList: PropTypes.array,
    followLead: PropTypes.object,
    onOpenAppointmentNotBooked: PropTypes.func,
    onOpenAppointmentBooked: PropTypes.func,
    onOpenUnableToContact: PropTypes.func,
    setLeadId: PropTypes.func,
    setTreatmentId: PropTypes.func,
    type: PropTypes.string,
    title: PropTypes.string,
    headerButtons: PropTypes.bool,
    useV2: PropTypes.bool,
    callType: PropTypes.oneOf(['inbound', 'outbound']),
    scriptCategory: PropTypes.oneOf(['INBOUND', 'OUTBOUND', 'TCO']),
};

export default CallScriptContainer;
